import { HttpInterceptorFn, HttpRequest } from "@angular/common/http";
import { inject } from "@angular/core";
import { AuthClientConfig, AuthHttpInterceptor, AuthService, HttpInterceptorRouteConfig, isHttpInterceptorRouteConfig } from "@auth0/auth0-angular";
import { first, switchMap } from "rxjs/operators";

/**
 * Wraps Auth0's interceptor so anonymous visitors don't hit Auth0 on every API call.
 *
 * For an `allowAnonymous` route, Auth0's interceptor always calls getTokenSilently(). With
 * `useRefreshTokensFallback: true` (app.config.ts) a visitor with no refresh token falls back to
 * the hidden-iframe `prompt=none` flow, which Auth0 answers with login_required — one round trip
 * and one "Failed Silent Auth" (fsa) tenant log entry per request, before the request is even sent.
 *
 * If the SDK has already settled on "not authenticated" (its startup checkSession found no
 * session), that round trip can't succeed, so send anonymous-route requests without a token.
 * Secured routes, and every request from an authenticated user, still go through Auth0's
 * interceptor unchanged. The cross-subdomain case the fallback exists for still works: checkSession
 * only runs when the SDK's is.authenticated cookie is present, and cookieDomain (app.config.ts)
 * shares that cookie across area subdomains, so on a newly opened area the startup checkSession
 * succeeds against the shared SSO session and isAuthenticated$ is true.
 */
export const anonymousAwareAuthInterceptor: HttpInterceptorFn = (req, next) => {
    // Resolve dependencies synchronously — the injection context is gone inside switchMap.
    const auth0Interceptor = inject(AuthHttpInterceptor);
    const allowedList = inject(AuthClientConfig).get()?.httpInterceptor?.allowedList ?? [];
    const delegate = () => auth0Interceptor.intercept(req, { handle: next });

    const route = allowedList.find((r) => matchesRoute(r, req));
    if (!route || !isHttpInterceptorRouteConfig(route) || !route.allowAnonymous) {
        return delegate();
    }

    // isAuthenticated$ only emits once isLoading$ is false, which Auth0's interceptor waits for anyway.
    return inject(AuthService).isAuthenticated$.pipe(
        first(),
        switchMap((isAuthenticated) => (isAuthenticated ? delegate() : next(req)))
    );
};

// Mirrors AuthHttpInterceptor.canAttachToken (private in @auth0/auth0-angular) so the first-match
// route we find here is the same one Auth0's interceptor would pick.
function matchesRoute(route: string | HttpInterceptorRouteConfig, req: HttpRequest<unknown>): boolean {
    const testPrimitive = (value: string | undefined) => {
        if (!value) {
            return false;
        }
        if (value === stripQueryFrom(req.url)) {
            return true;
        }
        return value.indexOf("*") === value.length - 1 && req.url.startsWith(value.slice(0, -1));
    };
    if (isHttpInterceptorRouteConfig(route)) {
        if (route.httpMethod && route.httpMethod !== req.method) {
            return false;
        }
        return route.uriMatcher ? route.uriMatcher(req.url) : testPrimitive(route.uri);
    }
    return testPrimitive(route);
}

function stripQueryFrom(uri: string): string {
    return uri.split("?")[0].split("#")[0];
}
